import type { AuthError, User } from '@supabase/supabase-js';

/**
 * When email confirmation is enabled, Supabase often returns **no error** for duplicate signups
 * (to avoid email enumeration). The response looks like a success but includes a stub user with
 * an empty `identities` array and no session.
 * @see https://supabase.com/docs/reference/javascript/auth-signup
 */
export function isLikelyDuplicateEmailSignUpResponse(user: User | null, session: unknown): boolean {
  if (session) return false;
  if (!user) return false;
  const identities = user.identities;
  return Array.isArray(identities) && identities.length === 0;
}

/**
 * Supabase / GoTrue returns different messages for "email already in use" depending on version and settings.
 */
export function isSignUpEmailAlreadyRegistered(error: AuthError | null | undefined): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const code = String((error as { code?: string }).code || '').toLowerCase();

  if (code === 'user_already_exists' || code === 'email_exists') return true;
  if (msg.includes('already registered')) return true;
  if (msg.includes('already exists')) return true;
  if (msg.includes('user already')) return true;
  if (msg.includes('email') && msg.includes('already')) return true;
  if (msg.includes('duplicate')) return true;
  // Some deployments return a generic DB message for unique violations
  if (msg.includes('unique') && msg.includes('violat')) return true;

  return false;
}
